import { useCallback } from 'react';

import type { UUID } from '@aline/common';
import { toDocParts } from '@aline/common';
import { useApolloClient } from '@apollo/client/react';
import { useDownloadDOCXWithTrackedChangesCheck } from 'src/editor/toolbars/menus/file/download';
import { Apollo } from 'src/gql/generated';
import { useLogAndDispatchErrorSnack } from 'src/helpers/snackbars';
import { useDownloadFileVersionWithLoading } from 'src/hooks';

/**
 * Downloads a workflow document that isn't open in the editor, from its latest saved version: an
 * unsigned Aline document as .docx with its tracked changes, anything else as its stored file.
 */
export function useDownloadTaskDocument(): {
  downloadDocument: (documentId: UUID) => Promise<void>;
} {
  const client = useApolloClient();
  const { downloadDOCXWithTrackedChangesCheck } = useDownloadDOCXWithTrackedChangesCheck();
  const { downloadFileVersionWithLoading } = useDownloadFileVersionWithLoading();
  const logAndDispatchError = useLogAndDispatchErrorSnack({
    message: "Couldn't download this document.",
  });

  const downloadDocument = useCallback(
    async (documentId: UUID) => {
      const { data } = await client.query<
        Apollo.GetTaskDocumentDownloadQuery,
        Apollo.GetTaskDocumentDownloadQueryVariables
      >({
        // A listed document can still be one the viewer can't open; that surfaces as no data.
        errorPolicy: 'all',
        fetchPolicy: 'network-only',
        query: Apollo.GetTaskDocumentDownloadDocument,
        variables: { documentId },
      });
      const document = data?.document;
      if (!document) {
        logAndDispatchError(new Error(`Document ${documentId} is not available to download.`));
        return;
      }

      const version = document.latestVersion;
      if (version.__typename !== 'DocumentVersion' || document.isSigned) {
        await downloadFileVersionWithLoading(documentId, {
          exportName: document.name,
          fileVersion: 'latestVersion',
        });
        return;
      }

      const docParts = toDocParts(version.documentJSON);
      if (!docParts) {
        logAndDispatchError(
          new Error(`Unreadable documentJSON on the latest version of ${documentId}.`)
        );
        return;
      }
      await downloadDOCXWithTrackedChangesCheck(documentId, {
        docParts,
        name: document.name || 'Untitled document',
      });
    },
    [
      client,
      downloadDOCXWithTrackedChangesCheck,
      downloadFileVersionWithLoading,
      logAndDispatchError,
    ]
  );

  return { downloadDocument };
}
